/**
 * data-agent attribute naming conventions
 *
 * Every interactive element that the AI agent can target must have a
 * `data-agent` HTML attribute. The value follows a prefix-based naming
 * scheme so that the agent can infer what kind of interaction is expected.
 *
 * ──────────────────────────────────────────────────────────────────────
 * PREFIX          PATTERN                         DESCRIPTION
 * ──────────────────────────────────────────────────────────────────────
 * action-create-  action-create-{resource}        Create button on a list page
 * action-submit-  action-submit-{resource}        Submit/create button on an editor (first save)
 * action-save-    action-save-{resource}          Save button on an editor (subsequent saves)
 * action-delete-  action-delete-{resource}        Delete button (triggers confirmation)
 * action-run      action-run                      Run/execute button (triggers confirmation)
 * action-stop     action-stop                     Stop/cancel execution
 * action-connect  action-connect                  Test connection button (connectors)
 * action-fetch-   action-fetch-{what}             Refresh/reload data button
 * action-extract- action-extract-{what}           Extract/process data button
 *
 * form-           form-{resource}-{field}         Named input field in an editor
 * form-field-     form-field-{key}                Dynamic auto-form field (from metas)
 *
 * store-item-     store-item-{id}                 Selectable card in Store modal
 * store-select    store-select                    Confirm selection in Store modal
 *
 * nav-service-    nav-service-{id}                Service link in global sidebar
 * sidebar-        sidebar-{element}               Sidebar UI control (toggle, home)
 * button-aside-   button-aside-{section}          Section link in module aside
 *
 * tree-           tree-{type}-{id}                Tree item in analyzer (database/table)
 * file-           file-{path}                     File/folder in file browser
 * ──────────────────────────────────────────────────────────────────────
 *
 * Rules:
 *
 * 1. IDs must be unique within the visible DOM at any given time.
 *    Dynamic IDs (e.g. store-item-{id}) use the resource identifier.
 *
 * 2. Destructive prefixes trigger a user confirmation modal before
 *    execution. See DESTRUCTIVE_PREFIXES in domExecutor.js.
 *
 * 3. Only elements with data-agent are targetable by the AI agent.
 *    The agent cannot interact with elements found by id, class, or
 *    other selectors (whitelist enforcement).
 *
 * 4. The agent discovers available elements via read_ui_state, which
 *    returns all visible data-agent values. Name them descriptively
 *    so the agent can infer their purpose without extra context.
 */

export const PREFIXES = {
  // Actions
  ACTION_CREATE: 'action-create-',
  ACTION_SUBMIT: 'action-submit-',
  ACTION_SAVE: 'action-save-',
  ACTION_DELETE: 'action-delete-',
  ACTION_RUN: 'action-run',
  ACTION_STOP: 'action-stop',
  ACTION_CONNECT: 'action-connect',
  ACTION_FETCH: 'action-fetch-',
  ACTION_EXTRACT: 'action-extract-',

  // Forms
  FORM: 'form-',
  FORM_FIELD: 'form-field-',

  // Store modal
  STORE_ITEM: 'store-item-',
  STORE_SELECT: 'store-select',

  // Navigation
  NAV_SERVICE: 'nav-service-',
  SIDEBAR: 'sidebar-',
  BUTTON_ASIDE: 'button-aside-',

  // Trees & files
  TREE: 'tree-',
  FILE: 'file-'
}

/**
 * Prefixes that require user confirmation before execution.
 * Must stay in sync with DESTRUCTIVE_PREFIXES in domExecutor.js.
 */
export const DESTRUCTIVE_PREFIXES = ['action-delete', 'action-submit', 'action-run']
